<script setup lang="ts">
// Logo Wac Buddy (design/logo/README.md): dva obrázky přepínané CSS podle režimu, takže nebliká.
// Režim určuje `data-theme` na <html>: 'light' | 'dark', u volby „systém“ atribut chybí
// a rozhoduje prefers-color-scheme (stejně jako tokeny v design/tokens.css).
// Pravidla: horizontální logo min. 24 px výšky, symbol min. 16 px, ochranná zóna kolem loga
// je výška písmene B (řeší místo použití odsazením). Svit (logo-horizontal-dark.svg) jen na wacbuddy.cz.
// Tmavá stacked varianta (logo-stacked-dark.svg) má svit (feGaussianBlur), do aplikace nepatří:
// varianta `stacked` je určená pro marketingový web, obrazovky aplikace používají `horizontal`.
// Minimální výšky vynucuje komponenta (horizontální 24, symbol 16, stacked 48).
import { computed } from 'vue'

type Variant = 'horizontal' | 'stacked' | 'symbol'

const props = withDefaults(
  defineProps<{
    variant?: Variant
    /** Výška v px; výchozí 28 (horizontální), 72 (stacked), 32 (symbol). */
    height?: number
    /** Alternativní text. Prázdný, když je vedle loga text s názvem. */
    alt?: string
    /** Vždy verze pro tmavou plochu (např. avatar na tmavém kruhu), bez ohledu na režim. */
    onDark?: boolean
  }>(),
  { variant: 'horizontal', height: undefined, alt: '', onDark: false },
)

const SOURCES: Record<
  Variant,
  { light: string; dark: string; ratio: number; height: number; min: number }
> = {
  horizontal: {
    light: '/logo-horizontal-light.svg',
    dark: '/logo-horizontal-dark-noglow.svg',
    ratio: 735 / 120,
    height: 28,
    min: 24,
  },
  stacked: {
    light: '/logo-stacked-light.svg',
    dark: '/logo-stacked-dark.svg',
    ratio: 581 / 264,
    height: 72,
    min: 48,
  },
  symbol: {
    light: '/symbol-light-bg.svg',
    dark: '/symbol-dark-bg.svg',
    ratio: 1,
    height: 32,
    min: 16,
  },
}

const source = computed(() => SOURCES[props.variant])
const h = computed(() => Math.max(props.height ?? source.value.height, source.value.min))
const w = computed(() => Math.round(h.value * source.value.ratio))
</script>

<template>
  <span class="brand-logo" data-testid="brand-logo" :data-variant="variant">
    <img
      v-if="!onDark"
      class="brand-logo__img brand-logo__img--light"
      :src="source.light"
      :alt="alt"
      :width="w"
      :height="h"
      :style="{ width: `${w}px`, height: `${h}px` }"
    />
    <img
      class="brand-logo__img brand-logo__img--dark"
      :class="{ 'brand-logo__img--always': onDark }"
      :src="source.dark"
      :alt="alt"
      :width="w"
      :height="h"
      :style="{ width: `${w}px`, height: `${h}px` }"
    />
  </span>
</template>

<style>
/* Ve vrstvě components, aby je utility z místa použití (hidden, sm:inline-flex) přebily. */
@layer components {
  .brand-logo {
    display: inline-flex;
    flex-shrink: 0;
    line-height: 0;
  }
}
.brand-logo__img {
  display: block;
  max-width: none;
}
.brand-logo__img--dark:not(.brand-logo__img--always) {
  display: none;
}
html[data-theme='dark'] .brand-logo__img--light {
  display: none;
}
html[data-theme='dark'] .brand-logo__img--dark {
  display: block;
}
@media (prefers-color-scheme: dark) {
  html:not([data-theme='light']) .brand-logo__img--light {
    display: none;
  }
  html:not([data-theme='light']) .brand-logo__img--dark {
    display: block;
  }
}
</style>
